import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { code: "", type: "percent", value: 10, minOrderValue: 0, maxDiscount: null, isActive: true };

export default function AdminCoupons() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = () => api.get("/admin/coupons").then(({ data }) => setItems(data || []));
    useEffect(() => { refresh(); }, []);

    const startCreate = () => { setEditing({ ...EMPTY }); setOpen(true); };
    const startEdit = (c) => { setEditing({ ...EMPTY, ...c }); setOpen(true); };
    const close = () => { setOpen(false); setEditing(null); };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...editing,
                value: Number(editing.value) || 0,
                minOrderValue: Number(editing.minOrderValue) || 0,
                maxDiscount: editing.maxDiscount ? Number(editing.maxDiscount) : null,
            };
            if (editing.id) {
                await api.patch(`/admin/coupons/${editing.id}`, payload);
                toast.success("Coupon updated");
            } else {
                await api.post("/admin/coupons", payload);
                toast.success("Coupon created");
            }
            close();
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to save");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this coupon?")) return;
        try {
            await api.delete(`/admin/coupons/${id}`);
            toast.success("Coupon deleted");
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to delete"); }
    };

    return (
        <div data-testid="admin-coupons-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Promotions</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Coupons</h1>
                    <p className="mt-1 text-inkMuted">{items.length} active offers in your boutique.</p>
                </div>
                <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-coupon-button"><Plus size={14} /> Add coupon</button>
            </div>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-coupons-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Code</th>
                                <th className="text-left px-4 py-3 font-medium">Type</th>
                                <th className="text-left px-4 py-3 font-medium">Value</th>
                                <th className="text-left px-4 py-3 font-medium">Min order</th>
                                <th className="text-left px-4 py-3 font-medium">Max discount</th>
                                <th className="text-left px-4 py-3 font-medium">Used</th>
                                <th className="text-left px-4 py-3 font-medium">Active</th>
                                <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-t border-inkDark/5">
                                    <td className="px-4 py-3 font-mono text-inkDark">{c.code}</td>
                                    <td className="px-4 py-3 capitalize">{c.type}</td>
                                    <td className="px-4 py-3">{c.type === 'percent' ? `${c.value}%` : `\u20B9${c.value}`}</td>
                                    <td className="px-4 py-3">\u20B9{c.minOrderValue || 0}</td>
                                    <td className="px-4 py-3">{c.maxDiscount ? `\u20B9${c.maxDiscount}` : "—"}</td>
                                    <td className="px-4 py-3 text-inkMuted">{c.usedCount}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full ${c.isActive ? 'bg-green-50 text-green-700' : 'bg-inkDark/5 text-inkMuted'}`}>{c.isActive ? "Live" : "Off"}</span></td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => startEdit(c)} className="p-2 rounded-lg hover:bg-beige" data-testid={`admin-coupon-edit-${c.code}`}><Pencil size={14} /></button>
                                        <button onClick={() => remove(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`admin-coupon-delete-${c.code}`}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-inkMuted">No coupons yet.</td></tr> : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={close} data-testid="admin-coupon-modal">
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-xl rounded-2xl shadow-lift">
                        <div className="p-6 border-b border-inkDark/5">
                            <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                            <h2 className="font-display text-2xl">{editing.id ? "Edit coupon" : "Add coupon"}</h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Code">
                                <input required value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 font-mono" data-testid="admin-coupon-code-input" />
                            </Field>
                            <Field label="Type">
                                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5">
                                    <option value="percent">Percent off</option>
                                    <option value="flat">Flat off (₹)</option>
                                </select>
                            </Field>
                            <Field label={editing.type === 'percent' ? "Percent (e.g. 10)" : "Flat amount (₹)"}>
                                <input type="number" required value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-coupon-value-input" />
                            </Field>
                            <Field label="Min order value (₹)">
                                <input type="number" value={editing.minOrderValue ?? 0} onChange={(e) => setEditing({ ...editing, minOrderValue: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Max discount (₹, optional)">
                                <input type="number" value={editing.maxDiscount ?? ""} onChange={(e) => setEditing({ ...editing, maxDiscount: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Active">
                                <button type="button" onClick={() => setEditing({ ...editing, isActive: !editing.isActive })} className={`w-full px-4 py-2.5 rounded-xl border ${editing.isActive ? "bg-skyblue/40 border-skyblue" : "bg-white border-inkDark/10 text-inkMuted"}`}>
                                    {editing.isActive ? "Live" : "Off"}
                                </button>
                            </Field>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={close} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="admin-coupon-save-button">{busy ? "Saving…" : "Save"}</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
